import { Injectable } from '@angular/core';
import { delay, from, mergeMap, Observable, retry, scan, takeLast, tap, throwError } from 'rxjs';
import { PartUpload, UploadPartTask } from '@shared/models';
import { UploadError } from '@shared/models/application-errors';

/**
 * Service for handling AWS S3 multipart uploads with presigned URLs.
 */
@Injectable({
  providedIn: 'root',
})
export class S3MultipartUploadService {
  private readonly CHUNK_SIZE = 100 * 1024 * 1024; // 100MB
  private readonly CONCURRENCY_LIMIT = 10; // max parallel uploads

  /**
   * Uploads a file to S3 using multipart upload with parallel strategy.
   * Progress updates are emitted as side effects during the upload.
   *
   * @param file - File or Blob to upload
   * @param presignedUrls - Array of presigned URLs for each part
   * @param onProgress - Optional callback for progress updates (0-100)
   * @returns Observable that emits the completed parts when upload finishes
   */
  uploadMultipart(
    file: File | Blob,
    presignedUrls: string[],
    onProgress?: (progress: number) => void,
  ): Observable<PartUpload[]> {
    const tasks = this.createUploadTasks(file, presignedUrls);

    /* Bytes per part, summed: a retried part reports from zero again and simply overwrites its own. */
    const sent = new Map<number, number>();
    const report = () => {
      let total = 0;
      sent.forEach((bytes) => (total += bytes));
      onProgress?.(Math.min(100, Math.round((100 * total) / (file.size || 1))));
    };

    return from(tasks).pipe(
      mergeMap(
        (task) =>
          this.uploadPart(task, (bytes) => {
            sent.set(task.partNumber, bytes);
            report();
          }).pipe(
            retry({
              count: 3,
              delay: (error, retryCount) => this.shouldRetry(error, task.partNumber, retryCount),
            }),
          ),
        this.CONCURRENCY_LIMIT,
      ),
      scan((acc: PartUpload[], part) => [...acc, part], []),
      takeLast(1),
      tap(() => onProgress?.(100)),
    );
  }

  /**
   * Creates upload tasks from presigned URLs.
   */
  private createUploadTasks(file: File | Blob, presignedUrls: string[]): UploadPartTask[] {
    return presignedUrls.map((url, index) => ({
      file,
      url,
      partNumber: index + 1,
      start: index * this.CHUNK_SIZE,
      end: Math.min((index + 1) * this.CHUNK_SIZE, file.size),
    }));
  }

  /**
   * Determines if an upload error should be retried with exponential backoff.
   */
  private shouldRetry(
    error: UploadError,
    partNumber: number,
    retryCount: number,
  ): Observable<never> {
    if (error?.status === 503 || error?.message?.includes('503')) {
      const delayMs = Math.pow(2, retryCount) * 1000; // 1s, 2s, 4s
      console.log(`Part ${partNumber} got 503, retrying in ${delayMs}ms...`);
      return throwError(() => error).pipe(delay(delayMs));
    }
    return throwError(() => error);
  }

  /**
   * Uploads a single part using native fetch API.
   *
   * @param task - The upload task containing file, URL and part metadata
   * @returns Observable that emits the part number and ETag
   */
  /*
   XHR and not `fetch`, which reports no upload progress at all: without it the bar could only count
   whole 100MB parts, so a 1GB file sat at 0% and jumped to 100% at the end. Unsubscribing aborts
   the request, which is what makes a cancelled import stop sending.
  */
  private uploadPart(
    task: UploadPartTask,
    onBytes?: (loaded: number) => void,
  ): Observable<PartUpload> {
    const chunk = task.file.slice(task.start, task.end);

    return new Observable<PartUpload>((observer) => {
      const request = new XMLHttpRequest();

      request.open('PUT', task.url, true);
      request.upload.onprogress = (event) => onBytes?.(event.loaded);

      request.onload = () => {
        if (request.status < 200 || request.status >= 300) {
          observer.error(
            new UploadError(`HTTP ${request.status}: ${request.statusText}`, request.status),
          );
          return;
        }

        const eTag = request.getResponseHeader('ETag')?.replace(/"/g, '') || '';
        if (!eTag) {
          console.warn(`Part ${task.partNumber}: ETag not found. Check S3 CORS configuration.`);
        }

        onBytes?.(chunk.size);
        observer.next({ partNumber: task.partNumber, eTag });
        observer.complete();
      };

      request.onerror = () => observer.error(new UploadError('Network error during upload', 0));

      request.send(chunk);

      return () => request.abort();
    });
  }
}
